import {
  CameraProfileOption,
  DiscoveredCamera,
  DiscoveryHost,
  OnvifVideoProfile,
} from "./onvifScanInterfaces";
import { deriveProfileStream } from "./deriveProfileStream";
import { parseRtspUri } from "./parseRtspUri";

const formatResolution = (
  width?: number,
  height?: number
): string | undefined => {
  if (width && height) {
    return `${width}x${height}`;
  }
  return undefined;
};

const NO_CHANNEL = "single";

/**
 * Pick the stream to onboard by default for one channel.
 *
 * Stream index wins over resolution: it is the vendor's own main/sub numbering
 * and stays correct even when the device misreports its encoder settings. Some
 * firmware answers every encoder query with one shared config, which makes every
 * profile look identical and turns a resolution-based choice into "whichever the
 * device happened to list first". Resolution is only the tiebreak.
 */
const pickPrimary = (
  options: CameraProfileOption[]
): CameraProfileOption => {
  const areaOf = (option: CameraProfileOption): number => {
    const [w, h] = (option.resolution || "0x0").split("x").map(Number);
    return (w || 0) * (h || 0);
  };

  return options.reduce((best, option) => {
    const bestIndex = best.streamIndex;
    const optionIndex = option.streamIndex;
    if (optionIndex != null && bestIndex != null && optionIndex !== bestIndex) {
      return optionIndex < bestIndex ? option : best;
    }
    if (optionIndex != null && bestIndex == null) {
      return option;
    }
    if (optionIndex == null && bestIndex != null) {
      return best;
    }
    return areaOf(option) > areaOf(best) ? option : best;
  });
};

const toProfileOption = (
  profile: OnvifVideoProfile,
  index: number
): CameraProfileOption => {
  const { streamIndex, role } = deriveProfileStream(profile);
  const token = profile.profile_token || String(index);
  const resolution = formatResolution(
    profile.resolution_width,
    profile.resolution_height
  );
  const path = parseRtspUri(profile.rtsp_uri).path;

  // The path is part of the label on purpose: when a device reports the same
  // resolution for every stream, the path is the only thing that distinguishes
  // "/cam/realmonitor?channel=2&subtype=0" from "...&subtype=2".
  //
  // A resolution the device's own encoder options exclude is known to be wrong
  // (some NVR firmware reports one shared config for every profile through every
  // ONVIF operation), so it is marked rather than stated as fact.
  const resolutionLabel =
    resolution && profile.resolution_supported === false
      ? `${resolution} (unverified)`
      : resolution;
  const labelParts = [role, resolutionLabel, profile.encoding].filter(Boolean);
  const label = labelParts.length > 0 ? labelParts.join(" · ") : token;

  return {
    token,
    rtspUri: profile.rtsp_uri as string,
    resolution,
    encoding: profile.encoding,
    label: path ? `${label} · ${path}` : label,
    streamIndex,
    role,
    path,
    resolutionSupported: profile.resolution_supported ?? null,
  };
};

/**
 * One selectable camera per channel, not per host and not per profile.
 *
 * A plain camera exposes several profiles (main/sub streams) for one sensor, so
 * collapsing them into one row is right. An NVR, however, fronts a camera per
 * channel and exposes several streams for each, so a host can legitimately yield
 * many cameras. Profiles are therefore grouped by the channel derived from the
 * device's own RTSP URI (see deriveProfileStream); devices that expose no channel
 * numbering fall back to the previous one-row-per-host behaviour.
 *
 * A host with no ONVIF profiles (e.g. auth failed) yields no row; there is
 * nothing to auto-add without an RTSP URL.
 */
export const flattenDiscoveredCameras = (
  hosts: DiscoveryHost[]
): DiscoveredCamera[] => {
  const cameras: DiscoveredCamera[] = [];

  (hosts || []).forEach((host) => {
    const profiles = (host.profiles || []).filter((p) => !!p.rtsp_uri);
    if (profiles.length === 0) {
      return;
    }

    // Group by channel, preserving the order the device returned them in.
    const byChannel = new Map<string, CameraProfileOption[]>();
    const channelNumbers = new Map<string, number | null>();
    profiles.forEach((profile, index) => {
      const { channel } = deriveProfileStream(profile);
      const key = channel == null ? NO_CHANNEL : String(channel);
      if (!byChannel.has(key)) {
        byChannel.set(key, []);
        channelNumbers.set(key, channel);
      }
      (byChannel.get(key) as CameraProfileOption[]).push(
        toProfileOption(profile, index)
      );
    });

    byChannel.forEach((profileOptions, channelKey) => {
      const channel = channelNumbers.get(channelKey) ?? null;
      const primary = pickPrimary(profileOptions);

      // Every stream reporting the same resolution means the device's encoder
      // data cannot distinguish them -- either genuinely identical streams or,
      // far more often, firmware answering every encoder query with one config.
      // Streams with no reported resolution (no encoder config, e.g. H.265 on
      // Media1) are left out: the device said nothing, so it contradicted
      // nothing.
      const reported = profileOptions.filter((option) => !!option.resolution);
      const distinctResolutions = new Set(
        reported.map((option) => option.resolution)
      );
      const resolutionsAmbiguous =
        (reported.length > 1 && distinctResolutions.size === 1) ||
        profileOptions.some((option) => option.resolutionSupported === false);

      cameras.push({
        key: channel == null ? host.ip : `${host.ip}::${channel}`,
        ip: host.ip,
        manufacturer: host.onvif?.manufacturer,
        model: host.onvif?.model,
        resolution: primary.resolution,
        rtspUri: primary.rtspUri,
        onvifUsernameUsed: host.onvif_username_used ?? null,
        credentialIndex: host.onvif_credential_index ?? null,
        profileToken: primary.token,
        channel,
        resolutionsAmbiguous,
        profiles: profileOptions,
      });
    });
  });

  return cameras;
};
